import { CircularProgress, Grid, makeStyles, Theme } from '@material-ui/core';
import { TimeSeriesValueType } from '@moderepo/mode-app-components';
import { SmartModuleType, TimeSeriesAggregation } from '@moderepo/mode-internal-apis';
import React, { useEffect, useMemo, useState } from 'react';
import { useConvertTimeControlParamsToFetchDataTimeRangeParams } from '.';
import { EntityUIComponentCollection, filterMetricsDefinitionsByMetricsExclusionCustomSettings, GridCustomSettings, isGridCustomSettings,
    isMetricsDefinitionAndMetricsExclusionCustomSettings, MetricsDefinitionsAndMetricsExclusionCustomSettings, useSelectTranslatedEntityClasses }
    from '..';
import { ExtGrid } from '../..';
import { BasicXYChart, BASIC_XY_CHART_TYPES } from '../../../utils/constants';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectEntity, useSelectProject } from '../../../utils/dataStateHooks';
import { SettingsErrorLogger } from '../../../utils/logger';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { isObjectLiteral, isValidSettingNumberValue, isValidSettingBooleanValue, isValidSettingStringValue }
    from '../../../utils/utils';
import { EntityUIComponentProps } from '../EntityDetailsLayouts';
import { MetricDefinitionViewContainer } from './MetricDefinitionViewContainer';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {},

        reloadDataMessage: {
            fontWeight   : 500,
            color        : theme.palette.text.primary,
            textAlign    : 'center',
            paddingTop   : theme.spacing(0.5),
            textTransform: 'capitalize',
        },
    };
}, { name: 'EntityMetricsViewUIComponent', index: 1 });



const MIN_DATA_COUNT = 5;
const MAX_DATA_COUNT = 500;
const MIN_RELOAD_DATA_FREQ_IN_SECS = 30;        // 30 seconds
const MAX_RELOAD_DATA_FREQ_IN_SECS = 3600;      // 1 hour



/**
 * Interface for EntityMetricsViewUIComponent's customSettings. These are the props/value that can be configured for the
 * 'EntityMetricsViewUIComponent' from the entityClass's uiSettings.
 */
export interface EntityMetricsViewUIComponentCustomSettings extends MetricsDefinitionsAndMetricsExclusionCustomSettings, GridCustomSettings {

    // The types of metrics value to show, 'Aggregated' or 'Raw'. If this attribute is set to 'Aggregated' then only NUMERIC metrics will be shown
    // because the API for fetching 'aggregated' data does not support TAG metrics. If this is set to 'Raw' or 'undefined' then both numeric
    // and tag metrics will be shown and the component will show the RAW data for these metrics.
    readonly showValueType?: TimeSeriesValueType | undefined;

    // The default aggregation function to use when fetching aggregated data and the metrics does not have aggregation set, non-kpi metrics.
    // For KPI metrics, they already have aggregation function set therefore we will be using the aggregation configured in the kpi metrics
    // instead of this setting. The default is AVG.
    readonly defaultAggregation?: TimeSeriesAggregation | undefined;

    // The number of data point to fetch when fetching Raw data. The value must be between min 5 and max 500
    readonly dataCount?: number | undefined;

    // Whether or not to show the metricsDefinition's Name
    readonly showMetricsDefinitionName?: boolean | undefined;

    // Whether or not to display the latest value in the top right corner. Default is FALSE
    readonly showLatestValue?: boolean | undefined;

    // Whether or not to apply the numeric value's range to the chart's Y Axis. This will force Y Axis to display data in this range
    readonly applyValueRange?: boolean | undefined;

    // How often to reload data in SECONDS. Default is NO reload. If a value is specified, it must be between MIN/MAX RELOAD_DATA_FREQ_IN_SECS
    readonly reloadDataFreqInSecs?: number | undefined;

    // The type of chart to use for NUMERIC metrics. The default is 'line' chart
    readonly chartType?: BasicXYChart | undefined;
}



/**
 * type guard function to check if an object is a type of EntityKPIMetricsViewUIComponentCustomSettings
 */
const isEntityMetricsViewUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityMetricsViewUIComponentCustomSettings => {
    const settings = obj as EntityMetricsViewUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.showValueType, false, false, Object.values(TimeSeriesValueType))) {
        return errorLogger?.logError({
            message: `"showValueType" must be undefined or one of the following [${
                Object.values(TimeSeriesValueType).map((value) => {
                    return `"${value}"`;
                }).join(', ')
            }]`,
            value: settings.showValueType,
            path : [...configPath, 'showValueType'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.defaultAggregation, false, false, Object.values(TimeSeriesAggregation))) {
        return errorLogger?.logError({
            message: `"defaultAggregation" must be undefined or one of the following [${
                Object.values(TimeSeriesAggregation).map((value) => {
                    return `"${value}"`;
                }).join(', ')
            }]`,
            value: settings.defaultAggregation,
            path : [...configPath, 'defaultAggregation'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.dataCount, false, false, MIN_DATA_COUNT, MAX_DATA_COUNT)) {
        return errorLogger?.logError({
            message: `"dataCount" must be undefined or an integer between ${MIN_DATA_COUNT} and ${MAX_DATA_COUNT}.`,
            value  : settings.dataCount,
            path   : [...configPath, 'dataCount'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.showMetricsDefinitionName)) {
        return errorLogger?.logError({
            message: '"showMetricsDefinitionName" must be undefined or a boolean.',
            value  : settings.showMetricsDefinitionName,
            path   : [...configPath, 'showMetricsDefinitionName'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.showLatestValue)) {
        return errorLogger?.logError({
            message: '"showLatestValue" must be undefined or a boolean.',
            value  : settings.showLatestValue,
            path   : [...configPath, 'showLatestValue'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.applyValueRange)) {
        return errorLogger?.logError({
            message: '"applyValueRange" must be undefined or a boolean.',
            value  : settings.applyValueRange,
            path   : [...configPath, 'applyValueRange'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.reloadDataFreqInSecs, false, false, MIN_RELOAD_DATA_FREQ_IN_SECS, MAX_RELOAD_DATA_FREQ_IN_SECS)) {
        return errorLogger?.logError({
            // eslint-disable-next-line max-len
            message: `"reloadDataFreqInSecs" must be undefined or an integer between ${MIN_RELOAD_DATA_FREQ_IN_SECS} and ${MAX_RELOAD_DATA_FREQ_IN_SECS}.`,
            value  : settings.reloadDataFreqInSecs,
            path   : [...configPath, 'reloadDataFreqInSecs'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.chartType, false, false, BASIC_XY_CHART_TYPES)) {
        return errorLogger?.logError({
            message: `"chartType" must be undefined or one of the following [${
                BASIC_XY_CHART_TYPES.map((chartType) => {
                    return `"${chartType}"`;
                }).join(', ')}].`,
            value: settings.chartType,
            path : [...configPath, 'chartType'],
        }) ?? false;
    }

    if (!isMetricsDefinitionAndMetricsExclusionCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    if (!isGridCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    return true;
};


export interface EntityMetricsViewUIComponentProps extends EntityUIComponentProps {
    readonly customSettings?: EntityMetricsViewUIComponentCustomSettings | undefined;
}



/**
 * This component display ALL metrics, Numeric/Tag metrics, for 1 entity.
 */
export const EntityMetricsViewUIComponent: React.FC<EntityMetricsViewUIComponentProps> = (props: EntityMetricsViewUIComponentProps) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const selectedEntity = useSelectEntity(props.projectId, props.entityId, false);
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);

    // Initialize the reloadData countdown timer if a value is provided. If not, set it to the highest value
    const [reloadDataCountdownTimer, setReloadDataCountdownTimer] = useState<number>(
        props.customSettings?.reloadDataFreqInSecs ?? Number.MAX_SAFE_INTEGER,
    );


    /**
     * Create the params that are required for fetching data based on the props.fetchDataParams. Since this component need to fetch data
     * based on some start/end time, it will convert the props.fetchDataParams to FetchDataByTimeRangeOptions.
     */
    const fetchDataOptions = useConvertTimeControlParamsToFetchDataTimeRangeParams(props.fetchDataParams);


    const classes = useStyle();


    // Find the entityClass object that the selectedEntity belonged to
    const selectedEntityClass = useMemo(() => {
        if (selectedEntity && entityClasses) {
            return entityClasses.find((entityClass) => {
                return entityClass.entityClass === selectedEntity.entityClass;
            });
        }
        return undefined;
    }, [entityClasses, selectedEntity]);



    /**
     * This useEffect will handle updating the countdown timer every 1 second
     */
    useEffect(() => {
        let timerId: number | undefined;

        // Create a timer to decrease the reloadDataCountdownTimer by 1 every second
        const reloadDataFreqInSec = props.customSettings?.reloadDataFreqInSecs;
        if (reloadDataFreqInSec !== undefined) {
            timerId = window.setInterval(() => {
                setReloadDataCountdownTimer((currentValue) => {
                    // When the countdown reaches 0, change the fetchDataStartTime to trigger the component to refetch data
                    // and also reset the reloadDataCountdownTimer
                    if (currentValue === 0) {
                        return reloadDataFreqInSec;
                    }
                    return Math.max(0, currentValue - 1);
                });
            }, 1000);
        }

        // Return a cleanup function that will clear the timerId when the component unload so that it doesn't continue to run
        // after the user leave this page
        return () => {
            window.clearInterval(timerId);
        };
    }, [props.customSettings?.reloadDataFreqInSecs]);



    /**
      * An entity can have more then 1 metrics definitions. Not all metrics definitions need to be displayed because some
      * might need to be excluded based on the uiSettings. We we first need to filter out metricsDefinitions that we don't
      * need to display
      */
    const filteredMetricsDefinitions = useMemo(() => {
        // First, filter out all metricsDefinitions that are NOT TSDB. This component can only display charts for TSDB metrics definitions
        const tsdbMetricsDefinitions = selectedEntityClass?.metricsDefinitions?.filter((metricsDef) => {
            return metricsDef.dataStoreConfiguration.moduleType === SmartModuleType.TSDB;
        });

        // If we only need to show AGGREGATED metric type then we will filter out ALL metrics definition which don't have numeric metrics.
        // Else, we show all metrics definition of type TSDB
        const filteredMetricsDef = (props.customSettings?.showValueType === TimeSeriesValueType.AGGREGATED)
            ? tsdbMetricsDefinitions?.filter((metricsDef) => {
                return metricsDef.numericMetrics && metricsDef.numericMetrics.length > 0;
            })
            : tsdbMetricsDefinitions;

        return filterMetricsDefinitionsByMetricsExclusionCustomSettings(filteredMetricsDef ?? [], props.customSettings);
    }, [props.customSettings, selectedEntityClass?.metricsDefinitions]);


    return (
        <>
            {project && selectedEntity && selectedEntityClass && fetchDataOptions ? (
                <ExtGrid container xs_spacing={1} md_spacing={2} lg_spacing={3} className={classes.root}>
                    {props.customSettings?.reloadDataFreqInSecs !== undefined && (
                        <Grid item xs={12}>
                            <div className={classes.reloadDataMessage}>
                                {trans('label.next_update_in_time', {
                                    time: reloadDataCountdownTimer,
                                })}
                            </div>
                        </Grid>
                    )}

                    {filteredMetricsDefinitions.map((metricDef, index) => {
                        return (
                            <MetricDefinitionViewContainer
                                key={metricDef.metricsDefinitionId}
                                projectId={props.projectId}
                                entityId={props.entityId}
                                metricsDefinitionId={metricDef.metricsDefinitionId}
                                fetchDataOptions={fetchDataOptions}
                                chartIdPrefix={`EntityMetricsViewUIComponent-${props.entityId}-${metricDef.metricsDefinitionId}-${index}`}
                                metricsExclusionSettings={props.customSettings}
                                gridSizes={props.customSettings?.gridSizes}
                                showValueType={props.customSettings?.showValueType}
                                defaultAggregation={props.customSettings?.defaultAggregation}
                                dataCount={props.customSettings?.dataCount}
                                showMetricsDefinitionName={props.customSettings?.showMetricsDefinitionName}
                                showLatestValue={props.customSettings?.showLatestValue}
                                applyValueRange={props.customSettings?.applyValueRange}
                                chartType={props.customSettings?.chartType}
                            />
                        );
                    })}
                </ExtGrid>
            ) : (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent('EntityMetricsViewUIComponent', EntityMetricsViewUIComponent);


// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Metrics_Comp',
    {
        componentFunc          : EntityMetricsViewUIComponent,
        customSettingsValidator: isEntityMetricsViewUIComponentCustomSettings,
    },
);
